import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, Puzzle, X, Trash2, MousePointer, FileText, Pencil } from 'lucide-react'
import { renderAsync } from 'docx-preview'
import api from '../../../core/services/api'
import { logger } from '../../../core/services/logger'
import { Toast } from '../../../components/Toast'
import { TemplateDocumento } from '../../../types/template'

const LOG_COMPONENT = 'TemplateEditor'

// Caratteri di contesto da inviare al backend per disambiguare occorrenze multiple
const CONTEXT_CHARS = 40

export default function TemplateEditorPage() {
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()

  const [template, setTemplate] = useState<TemplateDocumento | null>(null)
  const [docxData, setDocxData] = useState<ArrayBuffer | null>(null)
  const [loading, setLoading] = useState(true)
  const [rendering, setRendering] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)

  // Placeholder disponibili
  const [placeholderDisponibili, setPlaceholderDisponibili] = useState<Record<string, string>>({})

  // Pannello placeholder flottante
  const [showPanel, setShowPanel] = useState(false)

  // Mostra/nascondi placeholder nell'anteprima
  const [showPlaceholders, setShowPlaceholders] = useState(true)

  // Selezione testo
  const [selectedText, setSelectedText] = useState('')
  const [contextBefore, setContextBefore] = useState('')
  const [contextAfter, setContextAfter] = useState('')

  // Modale scelta azione
  const [showActionModal, setShowActionModal] = useState(false)
  const [pendingPlaceholder, setPendingPlaceholder] = useState('')

  // Salvataggio
  const [saving, setSaving] = useState(false)

  // Context menu (tasto destro) per eliminare placeholder
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; placeholder: string } | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [placeholderToDelete, setPlaceholderToDelete] = useState('')
  const [deleting, setDeleting] = useState(false)

  // ONLYOFFICE editor
  const [editorMode, setEditorMode] = useState<'preview' | 'onlyoffice'>('preview')
  const [onlyofficeAvailable, setOnlyofficeAvailable] = useState(false)
  const [onlyofficeLoading, setOnlyofficeLoading] = useState(false)
  const onlyofficeRef = useRef<HTMLDivElement>(null)
  const onlyofficeEditorRef = useRef<any>(null)

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (templateId) {
      loadTemplate()
      loadPlaceholder()
    }
    checkOnlyofficeStatus()
  }, [templateId])

  // Cleanup ONLYOFFICE on unmount
  useEffect(() => {
    return () => {
      if (onlyofficeEditorRef.current) {
        try { onlyofficeEditorRef.current.destroyEditor() } catch {}
        onlyofficeEditorRef.current = null
      }
    }
  }, [])

  // Chiudi context menu al click ovunque
  useEffect(() => {
    const handleClick = () => setContextMenu(null)
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  // Intercetta tasto destro sui placeholder
  useEffect(() => {
    const handleRightClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const marker = target.closest('.placeholder-marker') as HTMLElement

      if (marker && contentRef.current?.contains(marker)) {
        e.preventDefault()
        e.stopPropagation()

        let placeholderName = marker.dataset.placeholder
        if (!placeholderName) {
          const text = marker.textContent || ''
          const match = text.match(/\{\{([^}]+)\}\}/)
          if (match) placeholderName = match[1]
        }

        if (placeholderName) {
          logger.debug('Tasto destro su placeholder', placeholderName, LOG_COMPONENT)
          setContextMenu({ x: e.clientX, y: e.clientY, placeholder: placeholderName })
        }
      }
    }

    document.addEventListener('contextmenu', handleRightClick, true)
    return () => document.removeEventListener('contextmenu', handleRightClick, true)
  }, [])

  // Render docx quando i dati cambiano o si togglano i placeholder
  useEffect(() => {
    if (docxData && contentRef.current) {
      renderDocxPreview()
    }
  }, [docxData, showPlaceholders])

  const loadTemplate = async () => {
    try {
      const [detailRes, binaryRes] = await Promise.all([
        api.get(`/v1/template-documenti/${templateId}`),
        api.get(`/v1/template-documenti/${templateId}/docx-preview-data`, {
          responseType: 'arraybuffer'
        })
      ])
      setTemplate(detailRes.data)
      setDocxData(binaryRes.data)
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      setToast({ message: err?.response?.data?.detail || 'Errore caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadPlaceholder = async () => {
    try {
      const res = await api.get('/v1/template-documenti/placeholder-disponibili')
      setPlaceholderDisponibili(res.data)
    } catch (error) {
      logger.error('Errore caricamento placeholder', error, LOG_COMPONENT)
    }
  }

  // ============ ONLYOFFICE ============

  const checkOnlyofficeStatus = async () => {
    try {
      const res = await api.get('/v1/onlyoffice/status')
      setOnlyofficeAvailable(res.data.available)
    } catch {
      setOnlyofficeAvailable(false)
    }
  }

  const loadOnlyofficeScript = (apiUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if ((window as any).DocsAPI) {
        resolve()
        return
      }
      const script = document.createElement('script')
      script.src = apiUrl
      script.onload = () => resolve()
      script.onerror = () => reject(new Error('Impossibile caricare ONLYOFFICE API'))
      document.head.appendChild(script)
    })
  }

  const openOnlyofficeEditor = async () => {
    if (!templateId) return
    setOnlyofficeLoading(true)

    try {
      const res = await api.get(`/v1/onlyoffice/config/${templateId}`)
      const { config, apiUrl } = res.data

      await loadOnlyofficeScript(apiUrl)

      setEditorMode('onlyoffice')

      // Attendi aggiornamento DOM
      await new Promise(r => setTimeout(r, 150))

      // Distruggi editor precedente se presente
      if (onlyofficeEditorRef.current) {
        try { onlyofficeEditorRef.current.destroyEditor() } catch {}
        onlyofficeEditorRef.current = null
      }

      // Inizializza editor ONLYOFFICE
      onlyofficeEditorRef.current = new (window as any).DocsAPI.DocEditor(
        'onlyoffice-editor-container',
        {
          ...config,
          events: {
            onAppReady: () => {
              logger.info('ONLYOFFICE editor pronto', undefined, LOG_COMPONENT)
            },
            onError: (event: any) => {
              logger.error('ONLYOFFICE errore', event, LOG_COMPONENT)
              setToast({ message: 'Errore nell\'editor ONLYOFFICE', type: 'error' })
            },
          },
        }
      )

    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } }; message?: string }
      logger.error('Errore apertura ONLYOFFICE', error, LOG_COMPONENT)
      setToast({
        message: err?.response?.data?.detail || err?.message || 'Errore apertura editor',
        type: 'error'
      })
      setEditorMode('preview')
    } finally {
      setOnlyofficeLoading(false)
    }
  }

  const closeOnlyofficeEditor = async () => {
    // Distruggi editor ONLYOFFICE
    if (onlyofficeEditorRef.current) {
      try { onlyofficeEditorRef.current.destroyEditor() } catch {}
      onlyofficeEditorRef.current = null
    }

    setEditorMode('preview')

    // Ricarica dati documento (ONLYOFFICE potrebbe aver salvato modifiche)
    setLoading(true)
    try {
      const [detailRes, binaryRes] = await Promise.all([
        api.get(`/v1/template-documenti/${templateId}`),
        api.get(`/v1/template-documenti/${templateId}/docx-preview-data`, {
          responseType: 'arraybuffer'
        })
      ])
      setTemplate(detailRes.data)
      setDocxData(binaryRes.data)
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      setToast({ message: err?.response?.data?.detail || 'Errore ricaricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // ============ DOCX-PREVIEW RENDERING ============

  const renderDocxPreview = async () => {
    if (!contentRef.current || !docxData) return

    setRendering(true)
    try {
      contentRef.current.innerHTML = ''

      await renderAsync(docxData, contentRef.current, undefined, {
        className: 'docx',
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        breakPages: true,
        ignoreLastRenderedPageBreak: true,
        useBase64URL: true,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true,
      })

      if (showPlaceholders) {
        highlightPlaceholders()
      }

      logger.debug('Docx-preview render completato', undefined, LOG_COMPONENT)
    } catch (error) {
      logger.error('Errore rendering docx-preview', error, LOG_COMPONENT)
      setToast({ message: 'Errore nel rendering del documento', type: 'error' })
    } finally {
      setRendering(false)
    }
  }

  // Evidenzia i {{placeholder}} nel DOM renderizzato da docx-preview
  const highlightPlaceholders = () => {
    if (!contentRef.current) return

    const walker = document.createTreeWalker(
      contentRef.current,
      NodeFilter.SHOW_TEXT,
      null
    )

    const textNodes: Text[] = []
    let node: Node | null
    while ((node = walker.nextNode())) {
      if (node.textContent && node.textContent.includes('{{')) {
        textNodes.push(node as Text)
      }
    }

    textNodes.forEach(textNode => {
      const text = textNode.textContent || ''
      const regex = /(\{\{[^}]+\}\})/g
      const parts = text.split(regex)

      if (parts.length <= 1) return

      const fragment = document.createDocumentFragment()
      parts.forEach(part => {
        const match = part.match(/^\{\{([^}]+)\}\}$/)
        if (match) {
          const span = document.createElement('span')
          span.className = 'placeholder-marker'
          span.dataset.placeholder = match[1]
          span.title = placeholderDisponibili[match[1]] || match[1]
          span.textContent = part
          fragment.appendChild(span)
        } else if (part) {
          fragment.appendChild(document.createTextNode(part))
        }
      })

      textNode.parentNode?.replaceChild(fragment, textNode)
    })
  }

  // Trova il testo originale dato un placeholder name
  const findOriginalText = useCallback((placeholderName: string): string | null => {
    if (!template?.placeholder_map) return null
    for (const [testoOriginale, mapping] of Object.entries(template.placeholder_map)) {
      const ph = typeof mapping === 'string' ? mapping : (mapping as { placeholder: string }).placeholder
      if (ph === placeholderName) return testoOriginale
    }
    return null
  }, [template?.placeholder_map])

  // ============ GESTIONE SELEZIONE TESTO ============

  const handleTextSelection = () => {
    const selection = window.getSelection()
    if (selection && selection.toString().trim() && contentRef.current?.contains(selection.anchorNode)) {
      const text = selection.toString().trim()
      setSelectedText(text)

      // Estrai contesto prima e dopo per disambiguare
      try {
        const range = selection.getRangeAt(0)
        const fullText = contentRef.current?.textContent || ''

        // Trova la posizione del testo selezionato nel contenuto completo
        const rangeStart = getTextOffset(contentRef.current, range.startContainer, range.startOffset)
        const rangeEnd = getTextOffset(contentRef.current, range.endContainer, range.endOffset)

        const before = fullText.slice(Math.max(0, rangeStart - CONTEXT_CHARS), rangeStart)
        const after = fullText.slice(rangeEnd, rangeEnd + CONTEXT_CHARS)

        setContextBefore(before)
        setContextAfter(after)

        logger.debug('Selezione con contesto', { text, before, after }, LOG_COMPONENT)
      } catch (e) {
        logger.warn('Errore estrazione contesto', e, LOG_COMPONENT)
        setContextBefore('')
        setContextAfter('')
      }
    } else {
      setSelectedText('')
      setContextBefore('')
      setContextAfter('')
    }
  }

  // Calcola offset testuale di un nodo nel contenitore
  const getTextOffset = (container: Node, targetNode: Node, targetOffset: number): number => {
    let offset = 0
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null)

    let node = walker.nextNode()
    while (node) {
      if (node === targetNode) {
        return offset + targetOffset
      }
      offset += node.textContent?.length || 0
      node = walker.nextNode()
    }
    return offset
  }

  // ============ CLICK SU PLACEHOLDER ============

  const handlePlaceholderClick = (placeholder: string) => {
    if (!selectedText) {
      setToast({ message: 'Seleziona prima del testo nel documento', type: 'warning' })
      return
    }

    setPendingPlaceholder(placeholder)
    setShowActionModal(true)
    logger.info('Modale aperta', { selectedText, placeholder, contextBefore, contextAfter }, LOG_COMPONENT)
  }

  // ============ INSERIMENTO PLACEHOLDER ============

  const replaceSelectedText = async () => {
    if (!selectedText || !pendingPlaceholder) return
    await saveMapping(selectedText, pendingPlaceholder, 'replace')
    setShowActionModal(false)
    clearSelection()
  }

  const insertAfterSelectedText = async () => {
    if (!selectedText || !pendingPlaceholder) return
    await saveMapping(selectedText, pendingPlaceholder, 'insert')
    setShowActionModal(false)
    clearSelection()
  }

  const clearSelection = () => {
    setSelectedText('')
    setContextBefore('')
    setContextAfter('')
    setPendingPlaceholder('')
    window.getSelection()?.removeAllRanges()
  }

  const saveMapping = async (
    testoOriginale: string,
    placeholder: string,
    modalita: 'replace' | 'insert'
  ) => {
    setSaving(true)
    logger.group('Salvataggio Mapping')
    logger.info('Parametri', { testoOriginale, placeholder, modalita, contextBefore, contextAfter }, LOG_COMPONENT)

    try {
      await api.post(`/v1/template-documenti/${templateId}/mapping-manuale`, {
        testo_originale: testoOriginale,
        placeholder,
        modalita,
        contesto_prima: contextBefore,
        contesto_dopo: contextAfter
      })

      logger.info('Mapping salvato', undefined, LOG_COMPONENT)
      setToast({ message: 'Placeholder inserito', type: 'success' })

      // Ricarica template detail e .docx binary
      const [detailRes, binaryRes] = await Promise.all([
        api.get(`/v1/template-documenti/${templateId}`),
        api.get(`/v1/template-documenti/${templateId}/docx-preview-data`, {
          responseType: 'arraybuffer'
        })
      ])
      setTemplate(detailRes.data)
      setDocxData(binaryRes.data)
      logger.info('Template ricaricato', undefined, LOG_COMPONENT)
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      logger.error('Errore salvataggio', err?.response?.data, LOG_COMPONENT)
      setToast({ message: err?.response?.data?.detail || 'Errore salvataggio', type: 'error' })
    } finally {
      setSaving(false)
      logger.groupEnd()
    }
  }

  // ============ ELIMINAZIONE PLACEHOLDER ============

  const handleDeletePlaceholder = async () => {
    logger.group('Eliminazione Placeholder')
    logger.info('Avvio', { placeholderToDelete, placeholder_map: template?.placeholder_map }, LOG_COMPONENT)

    if (!placeholderToDelete) {
      logger.error('placeholderToDelete vuoto', undefined, LOG_COMPONENT)
      logger.groupEnd()
      return
    }

    const testoOriginale = findOriginalText(placeholderToDelete)
    logger.debug('Testo originale trovato', { testoOriginale }, LOG_COMPONENT)

    setDeleting(true)
    try {
      // Se c'è mapping, usa testo_originale; altrimenti usa placeholder per rimozione diretta
      const body = testoOriginale
        ? { testo_originale: testoOriginale }
        : { placeholder: placeholderToDelete }

      logger.info('Parametri API', body, LOG_COMPONENT)

      await api.post(`/v1/template-documenti/${templateId}/rimuovi-mapping`, body)

      setToast({ message: 'Placeholder rimosso', type: 'success' })
      setShowDeleteModal(false)
      setPlaceholderToDelete('')

      const [detailRes, binaryRes] = await Promise.all([
        api.get(`/v1/template-documenti/${templateId}`),
        api.get(`/v1/template-documenti/${templateId}/docx-preview-data`, {
          responseType: 'arraybuffer'
        })
      ])
      setTemplate(detailRes.data)
      setDocxData(binaryRes.data)
      logger.info('Eliminazione completata', undefined, LOG_COMPONENT)
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } }
      logger.error('Errore eliminazione', err?.response?.data, LOG_COMPONENT)
      setToast({ message: err?.response?.data?.detail || 'Errore eliminazione', type: 'error' })
    } finally {
      setDeleting(false)
      logger.groupEnd()
    }
  }

  // ============ RENDERING ============

  // Raggruppa placeholder per categoria
  const groupedPlaceholders = () => {
    const groups: Record<string, { key: string; desc: string }[]> = {
      'Cliente': [],
      'Referente': [],
      'Offerta': [],
      'Consegna': [],
      'Azienda': [],
      'Tabelle': [],
      'Speciali': [],
    }

    Object.entries(placeholderDisponibili).forEach(([key, desc]) => {
      if (key.startsWith('clienti.')) groups['Cliente'].push({ key, desc })
      else if (key.startsWith('referenti_clienti.')) groups['Referente'].push({ key, desc })
      else if (key.startsWith('offerte.') && !key.includes('consegna')) groups['Offerta'].push({ key, desc })
      else if (key.includes('consegna')) groups['Consegna'].push({ key, desc })
      else if (key.startsWith('tenants.')) groups['Azienda'].push({ key, desc })
      else if (key.startsWith('offerte_') || key.startsWith('listino.')) groups['Tabelle'].push({ key, desc })
      else if (key === 'FOTO_OFFERTA' || key === 'SCHEDE_PRODOTTO') groups['Speciali'].push({ key, desc })
    })

    return Object.entries(groups).filter(([, items]) => items.length > 0)
  }

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        {/* Skeleton header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-9 h-9 rounded-lg bg-gray-200 skeleton-shimmer" />
          <div className="flex-1">
            <div className="h-6 w-48 rounded bg-gray-200 skeleton-shimmer mb-2" />
            <div className="h-4 w-32 rounded bg-gray-100 skeleton-shimmer" />
          </div>
        </div>
        {/* Skeleton istruzioni */}
        <div className="h-10 rounded-lg bg-blue-50 skeleton-shimmer mb-4" />
        {/* Skeleton documento */}
        <div className="flex-1 rounded-xl border border-gray-200 bg-gray-100 flex justify-center p-8">
          <div className="w-full max-w-[640px] bg-white rounded shadow-md p-10 space-y-4">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-5 h-5 text-gray-300" />
              <div className="h-4 w-40 rounded bg-gray-200 skeleton-shimmer" />
            </div>
            {[100, 85, 92, 70, 88, 95, 60, 80, 90, 75, 85, 65].map((w, i) => (
              <div key={i} className="h-3 rounded bg-gray-100 skeleton-shimmer" style={{ width: `${w}%` }} />
            ))}
            <div className="h-6" />
            {[90, 78, 95, 82, 68].map((w, i) => (
              <div key={`b${i}`} className="h-3 rounded bg-gray-100 skeleton-shimmer" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
          }
          .skeleton-shimmer {
            background: linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.04) 40%, rgba(0,0,0,0.04) 60%, transparent 100%) no-repeat;
            background-size: 800px 100%;
            animation: shimmer 1.5s infinite ease-in-out;
          }
        `}</style>
      </div>
    )
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Template non trovato</p>
        <button onClick={() => navigate('/app/configurazione/modelli-stampa')} className="mt-4 text-primary-600 hover:underline">
          Torna alla lista
        </button>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/app/configurazione/modelli-stampa')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Editor Template</h1>
            <p className="text-gray-500">{template.nome}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {editorMode === 'preview' && (
            <button
              onClick={() => setShowPlaceholders(!showPlaceholders)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                showPlaceholders ? 'bg-yellow-50 border-yellow-300 text-yellow-700' : 'bg-white border-gray-300 text-gray-600'
              }`}
            >
              {showPlaceholders ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Placeholder
            </button>
          )}

          {onlyofficeAvailable && (
            editorMode === 'preview' ? (
              <button
                onClick={openOnlyofficeEditor}
                disabled={onlyofficeLoading}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-50"
              >
                {onlyofficeLoading ? (
                  <span className="flex gap-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                ) : (
                  <Pencil className="w-4 h-4" />
                )}
                Apri Editor
              </button>
            ) : (
              <button
                onClick={closeOnlyofficeEditor}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Torna all'Anteprima
              </button>
            )
          )}
        </div>
      </div>

      {/* Istruzioni (solo in anteprima) */}
      {editorMode === 'preview' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mb-4 text-sm text-blue-700">
          <strong>1.</strong> Seleziona del testo nel documento <strong>2.</strong> Clicca un placeholder dal pannello
          {selectedText && (
            <span className="ml-2 px-2 py-0.5 bg-green-200 rounded text-green-800 font-medium">
              Pronto: "{selectedText.slice(0, 25)}{selectedText.length > 25 ? '...' : ''}"
            </span>
          )}
          <span className="ml-4 text-blue-600">• Tasto destro su placeholder giallo per eliminarlo</span>
        </div>
      )}
      {/* ONLYOFFICE Editor — Fullscreen Overlay */}
      {editorMode === 'onlyoffice' && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-300">
            <div className="flex items-center gap-3">
              <Pencil className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-gray-800">
                {template.nome} — Modifica il documento come in Word. Salvataggio automatico.
              </span>
            </div>
            <button
              onClick={closeOnlyofficeEditor}
              className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Torna all'Anteprima
            </button>
          </div>
          <div className="flex-1">
            {onlyofficeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                <span className="flex gap-0.5">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                <span className="ml-2 text-gray-600">Caricamento editor...</span>
              </div>
            )}
            <div id="onlyoffice-editor-container" ref={onlyofficeRef} style={{ height: 'calc(100vh - 48px)', width: '100%' }} />
          </div>
        </div>
      )}

      {/* Documento — reso con docx-preview */}
      {editorMode === 'preview' && (
        <div className="flex-1 rounded-xl shadow-sm border border-gray-200 overflow-auto relative docx-preview-container">
          {rendering && (
            <div className="absolute inset-x-0 top-0 z-10">
              <div className="h-1 bg-gray-200 overflow-hidden rounded-t-xl">
                <div className="h-full bg-primary-500 rendering-progress" />
              </div>
              <div className="flex items-center justify-center pt-8">
                <div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm text-gray-500">Rendering documento...</span>
                </div>
              </div>
            </div>
          )}
          <div
            ref={contentRef}
            onMouseUp={handleTextSelection}
          />
        </div>
      )}

      {/* Pulsante flottante per aprire pannello (solo in anteprima) */}
      {editorMode === 'preview' && (
        <button
          onClick={() => setShowPanel(!showPanel)}
          className={`fixed right-6 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all z-40 ${
            showPanel ? 'bg-gray-600 hover:bg-gray-700' : 'bg-primary-600 hover:bg-primary-700'
          } text-white`}
          title={showPanel ? 'Chiudi pannello' : 'Apri pannello placeholder'}
        >
          {showPanel ? <X className="w-6 h-6" /> : <Puzzle className="w-6 h-6" />}
        </button>
      )}

      {/* Pannello Placeholder Flottante */}
      {showPanel && editorMode === 'preview' && (
        <div className="fixed right-24 top-1/2 -translate-y-1/2 w-80 max-h-[70vh] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col z-40 overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">Placeholder disponibili</h3>
            <p className="text-xs text-gray-500 mt-1">
              {selectedText
                ? <span className="text-green-600">Clicca per applicare alla selezione</span>
                : 'Seleziona prima del testo nel documento'
              }
            </p>
          </div>

          <div className="flex-1 overflow-auto p-3 space-y-4">
            {groupedPlaceholders().map(([group, items]) => (
              <div key={group}>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{group}</h4>
                <div className="grid grid-cols-1 gap-1.5">
                  {items.map(({ key, desc }) => (
                    <button
                      key={key}
                      onClick={() => handlePlaceholderClick(key)}
                      disabled={!selectedText}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all border text-left ${
                        selectedText
                          ? 'bg-white border-gray-200 hover:border-primary-400 hover:bg-primary-50 cursor-pointer'
                          : 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <MousePointer className={`w-4 h-4 flex-shrink-0 ${selectedText ? 'text-primary-500' : 'text-gray-300'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{desc}</p>
                        <p className="text-xs text-gray-400 font-mono truncate">{`{{${key}}}`}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modale scelta azione (sostituisci/inserisci) */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Cosa vuoi fare?</h2>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Testo selezionato:</p>
              <p className="font-medium text-gray-900">"{selectedText.slice(0, 50)}{selectedText.length > 50 ? '...' : ''}"</p>
              {(contextBefore || contextAfter) && (
                <p className="text-xs text-gray-400 mt-1 font-mono">
                  ...{contextBefore.slice(-15)}<span className="text-primary-600 font-bold">[QUI]</span>{contextAfter.slice(0, 15)}...
                </p>
              )}
            </div>

            <div className="mb-4 p-3 bg-primary-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Placeholder:</p>
              <code className="text-sm font-medium text-primary-700">{`{{${pendingPlaceholder}}}`}</code>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={replaceSelectedText}
                disabled={saving}
                className="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <span className="flex gap-0.5"><span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{animationDelay:'0ms'}}/><span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{animationDelay:'150ms'}}/><span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{animationDelay:'300ms'}}/></span>}
                Sostituisci il testo
              </button>

              <button
                onClick={insertAfterSelectedText}
                disabled={saving}
                className="w-full px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <span className="flex gap-0.5"><span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{animationDelay:'0ms'}}/><span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{animationDelay:'150ms'}}/><span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{animationDelay:'300ms'}}/></span>}
                Inserisci dopo il testo
              </button>

              <button
                onClick={() => {
                  setShowActionModal(false)
                  clearSelection()
                }}
                className="w-full px-4 py-2 text-gray-500 hover:text-gray-700"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu (tasto destro su placeholder) */}
      {contextMenu && (
        <div
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation()
              setPlaceholderToDelete(contextMenu.placeholder)
              setShowDeleteModal(true)
              setContextMenu(null)
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 text-sm"
          >
            <Trash2 className="w-4 h-4" />
            Elimina placeholder
          </button>
        </div>
      )}

      {/* Modal conferma eliminazione */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Conferma Eliminazione</h2>

            <p className="text-gray-600 mb-4">Vuoi eliminare questo placeholder?</p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <code className="text-sm text-red-700">{`{{${placeholderToDelete}}}`}</code>
              <p className="text-xs text-gray-500 mt-1">{placeholderDisponibili[placeholderToDelete]}</p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setPlaceholderToDelete('')
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleDeletePlaceholder}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {deleting && <span className="flex gap-0.5"><span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{animationDelay:'0ms'}}/><span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{animationDelay:'150ms'}}/><span className="w-1 h-1 rounded-full bg-current animate-bounce" style={{animationDelay:'300ms'}}/></span>}
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stili per docx-preview + placeholder */}
      <style>{`
        .docx-preview-container {
          background: #e8e8e8;
        }
        .docx-preview-container .docx-wrapper {
          background: #e8e8e8;
          padding: 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .docx-preview-container .docx-wrapper > section.docx {
          box-shadow: 0 2px 12px rgba(0,0,0,0.15);
          margin-bottom: 20px;
          background: white;
        }
        .placeholder-marker {
          background-color: #fef08a;
          border-bottom: 2px solid #eab308;
          padding: 1px 4px;
          border-radius: 3px;
          cursor: context-menu;
          transition: all 0.15s ease;
          font-family: monospace;
          font-size: 0.9em;
        }
        .placeholder-marker:hover {
          background-color: #fde047;
          box-shadow: 0 0 0 2px rgba(234, 179, 8, 0.3);
        }
        @keyframes progress-indeterminate {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(250%); }
        }
        .rendering-progress {
          width: 40%;
          animation: progress-indeterminate 1.2s ease-in-out infinite;
        }
      `}</style>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
